import { getDashboardConfig } from '@store/actions/outboundCallingActions';
import { ALERT_STATUS_TYPE, SORT_ORDER } from '@constants';
import { INPUT_FIELD_TYPE } from '@components/DynamicFormField/constant';
import { formatNumberWithCommas } from '@utils';
import { formatDate } from '@utils/dateUtils';
import {
  AGENT_STATUS,
  CALL_OUTCOME_STATUS,
  SLA_STATUS,
  TICKET_PRIORITY,
  CALLBACK_REQUEST_STATUS,
  CALLBACK_REQUEST_STATUS_VALUES,
  STATUS_CARD_IDS
} from '@views/OutboundCallingDashboard/constant';

/**
 * Color scheme mapping for status cards
 */
export const COLOR_SCHEMES = {
  green: 'green.400',
  blue: 'blue.400',
  orange: 'orange.400',
  yellow: 'yellow.400',
  red: 'red.400',
  purple: 'purple.400',
  cyan: 'cyan.400'
};

/**
 * Get call status color
 */
export const getCallStatusColor = (callStatus) => {
  switch (callStatus) {
    case 'FINISHED':
      return 'green';
    case 'MISSED':
    case 'NO_ANSWER':
      return 'yellow';
    case 'BUSY':
      return 'orange';
    default:
      return 'gray';
  }
};

/**
 * Format call status for display
 */
export const formatCallStatus = (callStatus) => {
  return callStatus.replace(/_/g, ' ');
};

/**
 * Outbound Calling Dashboard Config Builder
 */
export const outboundCallingDashboardConfigBuilder = () => {
  return {
    agentDashboard: {
      searches: [
        {
          display: 'Ticket ID',
          searchKey: 'ticketId',
          placeholder: 'Search By Ticket ID',
          isDefault: true
        },
        // NOTE :: Hiding this for demo
        // {
        //   display: 'Customer Name',
        //   searchKey: 'customerName',
        //   placeholder: 'Search by Customer Name',
        //   isDefault: false
        // },
        {
          display: 'Order ID',
          searchKey: 'referenceId',
          placeholder: 'Search by Order ID',
          isDefault: false
        }
      ],
      filters: [
        {
          title: 'Status',
          key: 'status',
          filters: CALLBACK_REQUEST_STATUS.filter((s) => s.isFilterable).map(
            (s) => s.value
          )
        }
        // {
        //   title: "Priority",
        //   key: "priority",
        //   filters: Object.keys(TICKET_PRIORITY)
        // },
        // {
        //   title: "SLA Status",
        //   key: "slaStatus",
        //   filters: Object.keys(SLA_STATUS)
        // }
      ],
      sorting: {
        defaultSortKey: 'created_at',
        defaultSortValue: 'DESC',
        options: [
          {
            key: 'updated_at',
            display: 'updated Date',
            values: Object.values(SORT_ORDER)
          },
          {
            key: 'priority',
            display: 'Priority',
            values: Object.values(SORT_ORDER)
          },
          {
            key: 'sla_deadline',
            display: 'SLA Deadline',
            values: Object.values(SORT_ORDER)
          }
        ]
      }
    },
    adminDashboard: {
      // Placeholder for admin dashboard config
      searches: [],
      filters: [],
      sorting: {
        defaultSortKey: 'created_at',
        defaultSortValue: 'DESC',
        options: []
      }
    }
  };
};

/**
 * General Config for Outbound Calling Dashboard
 */
export const generalConfig = {
  actions: {
    getDashboardConfig: () => getDashboardConfig()
  }
};

/**
 * Agent Status Update Form Configuration for DynamicInputModal
 */
export const AGENT_STATUS_UPDATE_FORM_CONFIG = [
  {
    key: 'isOnline',
    name: 'isOnline',
    label: 'Mark Yourself As',
    placeholder: 'Select status...',
    fieldType: INPUT_FIELD_TYPE.SELECT,
    options: [
      { value: true, label: 'Active' },
      { value: false, label: 'Inactive' }
    ],
    isEditable: true,
    isRequired: true
  }
];

/**
 * Call Log Form Configuration for DynamicInputModal
 */
export const CALL_LOG_FORM_CONFIG = [
  // {
  //   key: 'callDate',
  //   name: 'callDate',
  //   label: 'Call Date & Time',
  //   placeholder: '',
  //   fieldType: INPUT_FIELD_TYPE.INPUT,
  //   inputType: 'datetime-local',
  //   size: 'sm',
  //   value: dayjs().format('YYYY-MM-DDTHH:mm'),
  //   isEditable: true,
  //   isRequired: true
  // },
  {
    key: 'outcome',
    name: 'outcome',
    label: 'Call Outcome',
    placeholder: 'Select outcome',
    fieldType: INPUT_FIELD_TYPE.SELECT,
    size: 'sm',
    options: Object.entries(CALL_OUTCOME_STATUS).map(([key, value]) => ({
      value: value,
      label: key.replace(/_/g, ' ')
    })),
    value: '',
    isEditable: true,
    isRequired: true
  },
  // {
  //   key: 'duration',
  //   name: 'duration',
  //   label: 'Duration (min)',
  //   placeholder: '15',
  //   fieldType: INPUT_FIELD_TYPE.INPUT,
  //   inputType: 'number',
  //   size: 'sm',
  //   value: '',
  //   isEditable: true,
  //   isRequired: true
  // },
  {
    key: 'notes',
    name: 'notes',
    label: 'Call Notes',
    placeholder: 'Detailed notes about the call...',
    fieldType: INPUT_FIELD_TYPE.TEXTAREA,
    size: 'sm',
    rows: 5,
    maxLength: 500,
    value: '',
    isEditable: true,
    isRequired: true
  }
];

/**
 * Unified Ticket Update Form Configuration
 * Includes both status update and notes in one form
 */
export const TICKET_UPDATE_FORM_CONFIG = (currentStatus) => [
  {
    key: 'status',
    name: 'status',
    label: 'Ticket Status',
    placeholder: '',
    fieldType: INPUT_FIELD_TYPE.SELECT,
    size: 'sm',
    options: CALLBACK_REQUEST_STATUS.filter((s) => s.isTicketActionAllowed).map(
      (status) => ({
        value: status.value,
        label: status.label
      })
    ),
    value: currentStatus,
    isEditable: true,
    isRequired: true
  },
  {
    key: 'note',
    name: 'note',
    label: 'Add Note (Optional)',
    placeholder: 'Enter your note here...',
    fieldType: INPUT_FIELD_TYPE.TEXTAREA,
    size: 'sm',
    rows: 4,
    maxLength: 500,
    value: '',
    isEditable: true,
    isRequired: false
  }
];

/**
 * Filter configurations for the dashboard
 */
export const getFilterConfigs = () => [
  {
    id: 'status',
    label: 'Status',
    options: CALLBACK_REQUEST_STATUS.filter((s) => s.isFilterable).map(
      (status) => ({
        label: status.label,
        value: status.value
      })
    ),
    placeholder: 'All Statuses'
  },
  {
    id: 'priority',
    label: 'Priority',
    options: Object.entries(TICKET_PRIORITY).map(([key, value]) => ({
      label: key,
      value: value
    })),
    placeholder: 'All Priorities'
  }
];

/**
 * Search type configurations
 */
export const getSearchTypes = () => [
  {
    label: 'Ticket ID',
    value: 'ticketId',
    placeholder: 'Enter ticket ID...'
  },
  {
    label: 'Customer Name',
    value: 'customerName',
    placeholder: 'Enter customer name...'
  },
  {
    label: 'Order ID',
    value: 'orderId',
    placeholder: 'Enter order ID...'
  }
];

/**
 * Get Agent Status Color
 */
export const getAgentStatusColor = (agentStatus) => {
  switch (agentStatus) {
    case AGENT_STATUS.INACTIVE:
      return 'red';
    case AGENT_STATUS.ON_BREAK:
      return 'orange';
    case AGENT_STATUS.IN_CALL:
      return 'yellow';
    case AGENT_STATUS.ACTIVE:
      return 'green';
    default:
      return 'gray';
  }
};

export const getAgentStatusText = (agentStatus) => {
  switch (agentStatus) {
    case AGENT_STATUS.INACTIVE:
      return 'Inactive';
    case AGENT_STATUS.ON_BREAK:
      return 'On a break';
    case AGENT_STATUS.IN_CALL:
      return 'In a call';
    case AGENT_STATUS.ACTIVE:
      return 'Active';
    default:
      return 'unknown';
  }
};

export const getAgentStatusTogglePromptText = (agentStatus) => {
  if (agentStatus === AGENT_STATUS.ACTIVE) {
    return 'Are you sure you want to go offline?';
  } else {
    return 'Are you sure you want to set your status as Active?';
  }
};

/**
 * Get priority color
 */
export const getPriorityColor = (priority) => {
  switch (priority) {
    case TICKET_PRIORITY.CRITICAL:
      return 'red';
    case TICKET_PRIORITY.HIGH:
      return 'orange';
    case TICKET_PRIORITY.MEDIUM:
      return 'yellow';
    case TICKET_PRIORITY.LOW:
      return 'green';
    default:
      return 'gray';
  }
};

/**
 * Get SLA status color
 */
export const getSLAColor = (slaStatus) => {
  switch (slaStatus) {
    case SLA_STATUS.WITHIN_SLA:
      return 'green';
    case SLA_STATUS.APPROACHING_SLA:
      return 'yellow';
    case SLA_STATUS.BREACHED_SLA:
      return 'red';
    default:
      return 'gray';
  }
};

/**
 * Get status color
 */
export const getStatusColor = (status) => {
  switch (status) {
    case CALLBACK_REQUEST_STATUS_VALUES.ASSIGNED:
      return 'blue';
    case CALLBACK_REQUEST_STATUS_VALUES.RESOLVED:
      return 'green';
    case CALLBACK_REQUEST_STATUS_VALUES.EXPIRED:
      return 'gray';
    case CALLBACK_REQUEST_STATUS_VALUES.FAILED:
      return 'red';
    case CALLBACK_REQUEST_STATUS_VALUES.ON_CALL:
      return 'orange';
    default:
      return 'gray';
  }
};

/**
 * Format duration in minutes to readable string
 */
export const formatDuration = (minutes) => {
  if (!minutes) return 'N/A';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

/**
 * Ticket to Entity Mapper
 * Converts ticket data to table entity format
 */
const ticketToEntity = (ticket) => {
  return {
    data: [
      {
        title: 'Ticket ID',
        value: ticket?.ticketId,
        showViewAndEditIcon: true,
        position: {
          value: 'sticky',
          style: {
            position: 'sticky',
            zIndex: '1',
            left: '0',
            backgroundColor: '#111111',
            boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.3)'
          }
        },
        isCopyable: true,
        isCreatable: false,
        isEditable: false,
        name: 'ticketId'
      },
      // {
      //   title: 'Customer',
      //   value: ticket?.customerName,
      //   isCreatable: true,
      //   isEditable: false,
      //   name: 'customerName'
      // },
      {
        title: 'Contact Number',
        value: ticket?.userPhoneNumber,
        type: 'input',
        inputType: 'number',
        isCopyable: true,
        isCreatable: true,
        isEditable: false,
        name: 'userPhoneNumber'
      },
      {
        title: 'Status',
        value: ticket?.status,
        type: 'badge',
        color: getStatusColor(ticket?.status),
        isEditable: false,
        name: 'status'
      },
      {
        title: 'Created',
        value: ticket?.createdAt,
        maskValue: formatDate(ticket?.createdAt),
        type: 'text',
        isEditable: false,
        name: 'createdAt'
      },
      {
        title: 'Order ID',
        value: ticket?.referenceId,
        type: 'input',
        inputType: 'string',
        isCopyable: true,
        isCreatable: true,
        isEditable: false,
        name: 'referenceId'
      },
      {
        title: 'Amount',
        value: `${ticket?.orderAmount} ${ticket?.sourceCurrency || ''}`,
        maskValue: `${formatNumberWithCommas(ticket?.orderAmount)} ${ticket?.sourceCurrency || ''
          }`,
        type: 'input',
        inputType: 'string',
        isCreatable: true,
        isEditable: false,
        name: 'orderAmount'
      },
      // {
      //   title: 'SLA',
      //   value: ticket?.slaStatus,
      //   type: 'badge',
      //   color: getSLAColor(ticket?.slaStatus),
      //   isEditable: false,
      //   name: 'slaStatus'
      // },
      {
        title: 'Attempts',
        value: ticket?.callAttemptsCount || 0,
        type: 'text',
        isEditable: false,
        name: 'callAttemptsCount'
      }
    ],
    type: 'ticket',
    id: ticket?.ticketId || ticket?.ticketId
  };
};

/**
 * Convert array of tickets to entity format
 */
export const ticketsToEntity = (tickets) => {
  if (!tickets || !Array.isArray(tickets)) return [];
  return tickets.map(ticketToEntity);
};

/**
 * Format phone number for display
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return 'N/A';
  // Remove non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  // Format as +XX XXXXX XXXXX
  if (cleaned.length > 10) {
    const countryCode = cleaned.slice(0, -10);
    const rest = cleaned.slice(-10);
    const part1 = rest.slice(0, 5);
    const part2 = rest.slice(5);
    return `+${countryCode} ${part1} ${part2}`;
  }
  return phone;
};

/**
 * Map admin dashboard data to Call Support Overview StatusCard format
 */
export const getCallSupportOverviewData = (dashboardData) => {
  if (!dashboardData) return [];

  return [
    {
      id: STATUS_CARD_IDS.AVAILABLE_AGENTS,
      count: dashboardData.agentAvailability?.available || 0,
      title: 'Available Agents',
      icon: 'MdPersonAdd',
      colorScheme: 'green',
      description: 'Agents ready to take calls'
    },
    {
      id: STATUS_CARD_IDS.QUEUE_LENGTH,
      count: dashboardData.queueDetails?.length || 0,
      title: 'Queue Length',
      icon: 'MdQueue',
      colorScheme: 'orange',
      description: 'Tickets waiting in queue'
    },
    {
      id: STATUS_CARD_IDS.COMPLETED_CALLS,
      count: dashboardData.overallCallAnalytics?.completed || 0,
      title: 'Completed Calls',
      icon: 'MdCheckCircle',
      colorScheme: 'blue',
      description: 'Successfully completed'
    }
  ];
};

/**
 * Map admin dashboard data to Agent Availability StatusCard format
 */
export const getAgentAvailabilityData = (dashboardData) => {
  if (!dashboardData) return [];

  return [
    {
      id: 'agents-available',
      count: dashboardData.agentAvailability?.available || 0,
      title: 'Available',
      icon: 'MdPersonOutline',
      colorScheme: 'green',
      description: 'Ready for calls'
    },
    {
      id: 'agents-on-call',
      count: dashboardData.agentAvailability?.onCall || 0,
      title: 'On Call',
      icon: 'MdPhone',
      colorScheme: 'yellow',
      description: 'Currently on call'
    },
    {
      id: 'agents-offline',
      count: dashboardData.agentAvailability?.offline || 0,
      title: 'Offline',
      icon: 'MdPersonOff',
      colorScheme: 'red',
      description: 'Not available'
    }
  ];
};

/**
 * Map admin dashboard data to Call Analytics StatusCard format
 */
export const getCallAnalyticsData = (dashboardData) => {
  if (!dashboardData) return [];

  return [
    {
      id: 'total-calls',
      count: dashboardData.overallCallAnalytics?.total || 0,
      title: 'Total Calls',
      icon: 'MdCall',
      colorScheme: 'purple',
      description: 'All time calls'
    },
    {
      id: 'failed-calls',
      count: dashboardData.overallCallAnalytics?.failed || 0,
      title: 'Failed Calls',
      icon: 'MdError',
      colorScheme: 'red',
      description: 'Unsuccessful calls'
    }
  ];
};

/**
 * Get sort options for admin dashboards
 * @param {string} dashboardType - Type of dashboard ('queue' or 'closed')
 * @returns {array} - Array of sort options
 */
export const getAdminDashboardSortOptions = (dashboardType = 'queue') => {
  const commonOptions = [
    { value: 'createdAt-desc', label: 'Created At (Newest First)' },
    { value: 'createdAt-asc', label: 'Created At (Oldest First)' },
    { value: 'updatedAt-desc', label: 'Updated At (Newest First)' },
    { value: 'updatedAt-asc', label: 'Updated At (Oldest First)' },
    { value: 'assignedAt-desc', label: 'Assigned At (Newest First)' },
    { value: 'assignedAt-asc', label: 'Assigned At (Oldest First)' },
    { value: 'onCallAt-desc', label: 'On Call At (Newest First)' },
    { value: 'onCallAt-asc', label: 'On Call At (Oldest First)' }
  ];

  if (dashboardType === 'closed') {
    return [
      ...commonOptions,
      { value: 'resolvedAt-desc', label: 'Resolved At (Newest First)' },
      { value: 'resolvedAt-asc', label: 'Resolved At (Oldest First)' },
      { value: 'cancelledAt-desc', label: 'Cancelled At (Newest First)' },
      { value: 'cancelledAt-asc', label: 'Cancelled At (Oldest First)' },
      { value: 'failedAt-desc', label: 'Failed At (Newest First)' },
      { value: 'failedAt-asc', label: 'Failed At (Oldest First)' }
    ];
  }

  return commonOptions;
};
